import { ATTRAPPE, hole, aendere, sende } from "./klient";
import { alsPflichtfotoliste } from "@mrmachine/fach";
import type { Pflichtfotoliste } from "@mrmachine/fach";
import type { Aufnahmetermin } from "./typen";
import { uhrImBetrieb, tagKurz } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Checklisten, Vorbereitung und Aufgaben — die Formen und die Wege.

   **Dieselbe Schicht wie `routen.ts`, eine eigene Datei.** Auch hier
   wird die Sicht des Servers (`answer_kind`, `claimed_by`,
   `defect_resolved_at`) an einer Stelle in die der Oberfläche
   übersetzt, und keine Maske liest einen Spaltennamen. Getrennt steht
   es, weil `routen.ts` über fünftausend Zeilen hat und die drei Dinge
   hier zusammen entstanden sind (Migration 0107, Durchgang AD2 bis AD4).

   Die Startseite und die Tagesliste am Handy lesen ihre Aufgaben aus
   `GET /start` und `GET /handy/heute`; die Übersetzung einer Aufgabe
   steht trotzdem hier (`alsAufgabe`), damit beide dieselbe bekommen.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const alsObjekt = (wert: unknown): Roh =>
  wert && typeof wert === "object" && !Array.isArray(wert) ? (wert as Roh) : {};
const txt = (wert: unknown): string =>
  wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert);
const zahlOderNull = (wert: unknown): number | null => {
  if (wert === null || wert === undefined || wert === "") return null;
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
};

/** `07:30` in deutscher Zeit (N8-02). */
export function uhrVon(iso: string | null): string {
  return uhrImBetrieb(iso);
}

/** `13.10.` — das Jahr steht im Kopf, nicht in der Zeile. */
export function tagVon(iso: string | null): string {
  return tagKurz(iso); // deutscher Tag (N8-02)
}

/* ══════════════ Aufgaben ══════════════ */

export type Aufgabenart = "vorbereitung" | "uebergabe" | "ruecknahme" | "mangel" | "ortsaufnahme";
export type Aufgabenpool = "bediener" | "technik" | "disposition" | "aussendienst";
export type Beteiligter = { id: string; name: string };

/**
 * Eine Aufgabe, wie sie aus Belegung oder Ortsaufnahme folgt.
 *
 * **Abgeleitet, nicht gespeichert** (Migration 0107): Es gibt keine
 * Aufgabenzeile, nur wer eine genommen hat. `darfNehmen` und
 * `darfZurueckgeben` rechnet der Server — die Maske zeigt einen Knopf nur,
 * wenn die Route ihn auch annimmt.
 */
export type Aufgabe = {
  art: Aufgabenart;
  /** Für den React-Schlüssel: Art und Bezug zusammen sind eindeutig. */
  id: string;
  belegungId: string | null;
  aufnahmeId: string | null;
  ressourceId: string | null;
  kennung: string;
  typ: string;
  kunde: string;
  ort: string;
  ortschaft: string;
  vorgang: string;
  faelligIso: string | null;
  /** 0 heute, 1 morgen, negativ: liegt zurück. `null` ohne Tag. */
  tage: number | null;
  ueberfaellig: boolean;
  genommen: Beteiligter | null;
  bediener: Beteiligter[];
  pool: Aufgabenpool;
  meins: boolean;
  darfNehmen: boolean;
  darfZurueckgeben: boolean;
  /** Beim Mangel: die Fragen, auf die „Nein" kam. */
  maengel: string[];
  /**
   * Bei der Ortsaufnahme: was gemessen wird — `QM` und sein Wort „m²"
   * (19.09.2026). Sonst `null`. Die Zeile auf „Heute" und dem Start sagt damit,
   * **was** zu tun ist, nicht nur wo.
   */
  messung: { metric: string; einheit: string } | null;
  /**
   * Bei der Ortsaufnahme **mit Termin** (Migration 0180): wann und wer. Dann
   * ist `faelligIso` der Beginn des Termins und `tage` der Abstand zu seinem
   * Tag. Ohne Termin `null` — und `faelligIso` ist der Tag der Bestellung.
   */
  termin: Aufnahmetermin | null;
  /** Wann die Aufnahme bestellt wurde. Ohne Termin dasselbe wie `faelligIso`. */
  angelegtIso: string | null;
};

const ARTEN: Aufgabenart[] = ["vorbereitung", "uebergabe", "ruecknahme", "mangel", "ortsaufnahme"];
const POOLS: Aufgabenpool[] = ["bediener", "technik", "disposition", "aussendienst"];

function alsPerson(wert: unknown): Beteiligter | null {
  const o = alsObjekt(wert);
  return txt(o["id"]) ? { id: txt(o["id"]), name: txt(o["name"]) } : null;
}

export function alsAufgabe(z: Roh): Aufgabe {
  const art = ARTEN.find((a) => a === txt(z["art"])) ?? "uebergabe";
  const messung = alsObjekt(z["messung"]);
  const termin = alsObjekt(z["termin"]);
  return {
    art,
    id: `${art}-${txt(z["bezug_id"])}`,
    belegungId: txt(z["belegung_id"]) || null,
    aufnahmeId: txt(z["aufnahme_id"]) || null,
    ressourceId: txt(z["ressource_id"]) || null,
    kennung: txt(z["kennung"]),
    typ: txt(z["typ"]),
    kunde: txt(z["kunde"]),
    ort: txt(z["ort"]),
    ortschaft: txt(z["ortschaft"]),
    vorgang: txt(z["vorgang"]),
    faelligIso: txt(z["faellig"]) || null,
    tage: zahlOderNull(z["tage"]),
    ueberfaellig: z["ueberfaellig"] === true,
    genommen: alsPerson(z["genommen"]),
    bediener: alsListe(z["bediener"]).map(alsPerson).filter((p): p is Beteiligter => p !== null),
    pool: POOLS.find((p) => p === txt(z["pool"])) ?? "technik",
    meins: z["meins"] === true,
    darfNehmen: z["darf_nehmen"] === true,
    darfZurueckgeben: z["darf_zurueckgeben"] === true,
    maengel: alsListe(z["maengel"]).map(txt).filter(Boolean),
    messung: messung["metric"]
      ? { metric: txt(messung["metric"]), einheit: txt(messung["einheit"]) || txt(messung["metric"]) }
      : null,
    termin: txt(termin["von"]) && txt(termin["bis"])
      ? { von: txt(termin["von"]), bis: txt(termin["bis"]), person: txt(termin["person"]), personId: txt(termin["person_id"]) }
      : null,
    angelegtIso: txt(z["angelegt"]) || txt(z["faellig"]) || null,
  };
}

/** Was sich an einer Belegung nehmen lässt — die Ortsaufnahme geht über `/aufnahmen`. */
export type Belegungsaufgabe = Exclude<Aufgabenart, "ortsaufnahme">;

/**
 * `POST /aufgaben/nehmen` — ohne `wer` für sich, `wer: null` zurück in den
 * Pool, `wer: <id>` für jemand anderen (nur Disposition und Verwaltung).
 *
 * **`grund` gilt nur beim Zurückgeben** (0145, 18.09.2026) und ist
 * freiwillig. Er steht danach im Verlauf — für den, der den Einsatz als
 * Nächster nimmt. Beim Nehmen übergeht die API ihn still.
 */
export async function aufgabeNehmen(
  art: Belegungsaufgabe, belegungId: string, wer?: string | null, grund?: string | null,
  /** Der Hinweis auf eine fehlende oder abgelaufene Qualifikation ist gelesen (D-47). */
  bestaetigt = false,
): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>("/aufgaben/nehmen", {
    art, belegung_id: belegungId,
    ...(wer === undefined ? {} : { wer }),
    ...(grund ? { grund } : {}),
    ...(bestaetigt ? { qualifikation_bestaetigt: true } : {}),
  });
}

/* ══════════════ Checklisten ══════════════ */

export type Antwortart = "ja_nein" | "zahl" | "text" | "auswahl";
export const ANTWORTARTEN: Antwortart[] = ["ja_nein", "zahl", "text", "auswahl"];
const alsArt = (wert: unknown): Antwortart => ANTWORTARTEN.find((a) => a === txt(wert)) ?? "ja_nein";

/**
 * Eine Antwortmöglichkeit einer Auswahlfrage (Migration 0114).
 *
 * `mangel` ist „Nein ist ein Mangel" auf eine Auswahl übersetzt: Welche
 * Möglichkeit einen Mangel bedeutet, sagt der Betrieb je Möglichkeit.
 */
export type Moeglichkeit = { text: string; mangel: boolean };

function alsMoeglichkeiten(wert: unknown): Moeglichkeit[] {
  return alsListe(wert)
    .map((m) => ({ text: txt(m["text"]), mangel: m["mangel"] === true }))
    .filter((m) => m.text !== "");
}

/** Eine Frage im Designer — auch die stillgelegten. */
export type Checklistenfrage = {
  id: string;
  gruppeId: string | null;
  typId: string | null;
  /** Der Name der Gruppe oder des Typs, an dem sie hängt. */
  gruppe: string;
  typ: string;
  text: string;
  art: Antwortart;
  pflicht: boolean;
  /** Nur bei `auswahl` gefüllt, in ihrer Reihenfolge. */
  moeglichkeiten: Moeglichkeit[];
  reihenfolge: number;
  aktiv: boolean;
  /** Aus der Vorbelegung — fällt weg, sobald jemand den Text ändert. */
  beispiel: boolean;
  /**
   * **Gilt diese Frage dem Füllstand?** (Migration 0138, Durchgang B2) Dann
   * fragt die Übergabe ihn als Stufe und leitet die Antwort daraus ab, statt
   * ihn zweimal zu fragen. Bis dahin wurde das am Wortlaut erkannt — „Tank"
   * oder „Akku" darin —, und das verschluckte „Tankdeckel dicht?".
   */
  tankfrage: boolean;
};

function alsFrage(z: Roh): Checklistenfrage {
  return {
    id: txt(z["id"]),
    gruppeId: txt(z["category_id"]) || null,
    typId: txt(z["machine_type_id"]) || null,
    gruppe: txt(z["gruppe"]),
    typ: txt(z["typ"]),
    text: txt(z["text"]),
    art: alsArt(z["answer_kind"]),
    moeglichkeiten: alsMoeglichkeiten(z["options"]),
    pflicht: z["required"] === true,
    reihenfolge: zahlOderNull(z["sort_order"]) ?? 100,
    aktiv: z["active"] !== false,
    beispiel: z["is_example"] === true,
    tankfrage: z["tankfrage"] === true,
  };
}

/** Eine Frage, wie sie für ein Gerät gilt — mit der Ebene, die sie gestellt hat. */
export type GeltendeFrage = {
  id: string;
  text: string;
  art: Antwortart;
  pflicht: boolean;
  beispiel: boolean;
  /** Nur bei `auswahl` gefüllt, in ihrer Reihenfolge. */
  moeglichkeiten: Moeglichkeit[];
  /** 0 global · 1 Übergruppe · 2 Gruppe · 3 Typ */
  ebene: 0 | 1 | 2 | 3;
  herkunft: string;
  /** Gilt sie dem Füllstand? Dann fragt die Übergabe ihn einmal (0138). */
  tankfrage: boolean;
  /**
   * **Verlangt diese Frage ein Bild?** (18.09.2026) Luca: *„Bei den
   * Pflichtangaben: Das muss auch die Möglichkeit geben, dass du Fotos vom
   * Zustand erfassen kannst."* Fotografiert werden darf an jeder Frage; wo es
   * hier steht, ist das Bild die Angabe selbst, und ohne es lässt sich die
   * Vorbereitung nicht abschließen (`fotoPflicht` in `bausteine/Checkliste.tsx`).
   *
   * **Fehlt das Feld, ist es `false`** — die Maske sperrt dann nur dort, wo
   * ihre eigene Regel greift (ein Mangel). Sagt die API es später, gilt es
   * ohne weitere Änderung.
   */
  fotoPflicht: boolean;
};

function alsGeltendeFrage(z: Roh): GeltendeFrage {
  const ebene = zahlOderNull(z["ebene"]);
  return {
    id: txt(z["id"]),
    text: txt(z["text"]),
    art: alsArt(z["answer_kind"]),
    pflicht: z["required"] === true,
    beispiel: z["is_example"] === true,
    moeglichkeiten: alsMoeglichkeiten(z["options"]),
    ebene: ebene === 1 || ebene === 2 || ebene === 3 ? ebene : 0,
    herkunft: txt(z["herkunft"]),
    tankfrage: z["tankfrage"] === true,
    fotoPflicht: z["foto_pflicht"] === true,
  };
}

export async function checklisten(): Promise<Checklistenfrage[]> {
  if (ATTRAPPE) return [];
  return alsListe((await hole<Roh>("/checklisten"))["zeilen"]).map(alsFrage);
}

export async function checklisteGeltung(typId: string): Promise<GeltendeFrage[]> {
  if (ATTRAPPE) return [];
  const roh = await hole<Roh>(`/checklisten/geltung?typ=${encodeURIComponent(typId)}`);
  return alsListe(roh["zeilen"]).map(alsGeltendeFrage);
}

export type Frageneingabe = {
  text: string;
  art: Antwortart;
  pflicht: boolean;
  /** Nur bei `auswahl` — die API weist eine unpassende Liste ab. */
  moeglichkeiten?: Moeglichkeit[];
  /** Gilt sie dem Füllstand? Nur an einer Ja/Nein-Frage (Migration 0138). */
  tankfrage?: boolean;
  gruppeId?: string | null;
  typId?: string | null;
};

export async function frageAnlegen(e: Frageneingabe): Promise<Checklistenfrage> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Checkliste.");
  return alsFrage(await sende<Roh>("/checklisten", {
    text: e.text, art: e.art, pflicht: e.pflicht,
    // Das Kennzeichen der Tankfrage (0138) — nur an einer Ja/Nein-Frage.
    ...(e.tankfrage === undefined ? {} : { tankfrage: e.tankfrage }),
    moeglichkeiten: e.art === "auswahl" ? e.moeglichkeiten ?? [] : [],
    ...(e.gruppeId ? { gruppe_id: e.gruppeId } : {}),
    ...(e.typId ? { typ_id: e.typId } : {}),
  }));
}

export async function frageAendern(
  id: string,
  felder: Partial<Pick<Checklistenfrage,
    "text" | "art" | "pflicht" | "aktiv" | "moeglichkeiten" | "tankfrage">>,
): Promise<Checklistenfrage> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Checkliste.");
  return alsFrage(await aendere<Roh>(`/checklisten/${encodeURIComponent(id)}`, {
    ...(felder.text !== undefined ? { text: felder.text } : {}),
    ...(felder.art !== undefined ? { art: felder.art } : {}),
    ...(felder.pflicht !== undefined ? { pflicht: felder.pflicht } : {}),
    ...(felder.aktiv !== undefined ? { aktiv: felder.aktiv } : {}),
    ...(felder.moeglichkeiten !== undefined ? { moeglichkeiten: felder.moeglichkeiten } : {}),
    ...(felder.tankfrage !== undefined ? { tankfrage: felder.tankfrage } : {}),
  }));
}

/** Die Reihenfolge einer Liste, wie sie im Designer steht. */
export async function fragenSortieren(ids: string[]): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>("/checklisten/reihenfolge", { ids });
}

/* ══════════════ Die Vorbereitung ══════════════ */

export type Antwortwert = boolean | number | string | null;

export type Antwort = {
  frageId: string;
  text: string;
  art: Antwortart;
  pflicht: boolean;
  wert: Antwortwert;
  /** Bei `auswahl`: ob die gewählte Möglichkeit als Mangel markiert war (0114). */
  mangel: boolean;
};

export type Vorbereitung = {
  antworten: Antwort[];
  notiz: string;
  /* Die Fotos stehen nicht hier, sondern in `photo` — gelesen über
     `GET /fotos?belegung=…&kontext=vorbereitung` (Welle 2, 18.09.2026). */
  genommen: (Beteiligter & { am: string }) | null;
  erledigt: { name: string; am: string } | null;
  /** Ein „Nein" auf eine Ja/Nein-Frage — abgeleitet in der Datenbank. */
  mangel: boolean;
  mangelErledigt: { name: string; am: string } | null;
  /** Bei der Übergabe nachgezogen (18.09.2026) — erledigt im Augenblick der Ausgabe. */
  beiUebergabe: boolean;
  /**
   * Erledigt erst nach der Übergabe (05.10.2026) — die Übergabe wurde auf
   * einen Zeitpunkt davor korrigiert. Optional: Eine ältere API kennt es nicht.
   */
  nachUebergabe?: boolean;
  /** Der Zählerstand beim Vorbereiten (0119) — für das Protokoll am Auftrag (L1). */
  betriebsstunden?: number | null;
};

export type Vorbereitungsblatt = {
  belegung: {
    id: string;
    ressourceId: string;
    kennung: string;
    typ: string;
    kunde: string;
    ort: string;
    ortschaft: string;
    vorgang: string;
    /** Der Hinweis für den Hof am Vorgang (0284) — leer: keiner, kein Kasten. */
    hofhinweis?: string;
    vonIso: string | null;
    bisIso: string | null;
    ausgegeben: boolean;
    bediener: Beteiligter[];
  };
  fragen: GeltendeFrage[];
  vorbereitung: Vorbereitung | null;
  darfVorbereiten: boolean;
  darfMangelErledigen: boolean;
  /**
   * **Ist der Zählerstand Pflicht?** Luca, 18.09.2026: *„Bei Betriebsstunden:
   * Geh mal in die Checkliste. Die Betriebsstunden sind Pflicht. Das ist nicht
   * freiwillig oder optional."*
   *
   * **Ohne Angabe der API gilt `true`** — das ist die Ansage. Ein Gerät ohne
   * Zähler (ein Rüttler hat keinen) nimmt die Pflicht zurück, indem die API
   * `zaehler_pflicht: false` schickt; die Maske zeigt es dann sofort, ohne
   * dass hier etwas nachgezogen werden müsste.
   */
  zaehlerPflicht: boolean;
  /**
   * **Die Pflichtfotos dieses Geräts, je Protokollart** (0173) — die Liste des
   * Betriebs, verändert durch Gerätegruppe und Übergruppe. Ohne Angabe der API
   * gilt Lucas Standard: lieber einmal zu viel gefragt, und „Foto nicht
   * möglich" lässt niemanden hängen.
   */
  pflichtfotos: Pflichtfotoliste;
};

function alsVorbereitung(z: Roh | null): Vorbereitung | null {
  if (!z || !txt(z["id"])) return null;
  const genommen = alsPerson({ id: z["claimed_by"], name: z["claimed_name"] });
  return {
    antworten: alsListe(z["answers"]).map((a) => ({
      frageId: txt(a["frage_id"]),
      text: txt(a["text"]),
      art: alsArt(a["art"]),
      pflicht: a["pflicht"] === true,
      wert: typeof a["wert"] === "boolean" || typeof a["wert"] === "number" || typeof a["wert"] === "string"
        ? a["wert"] : null,
      mangel: a["mangel"] === true,
    })),
    notiz: txt(z["note"]),
    genommen: genommen ? { ...genommen, am: txt(z["claimed_at"]) } : null,
    erledigt: txt(z["done_at"]) ? { name: txt(z["done_name"]), am: txt(z["done_at"]) } : null,
    mangel: z["has_defect"] === true,
    mangelErledigt: txt(z["defect_resolved_at"])
      ? { name: txt(z["defect_resolved_name"]), am: txt(z["defect_resolved_at"]) }
      : null,
    beiUebergabe: z["bei_uebergabe"] === true,
    ...(z["nach_uebergabe"] === true ? { nachUebergabe: true } : {}),
    betriebsstunden: typeof z["betriebsstunden"] === "number" ? z["betriebsstunden"] : null,
  };
}

/**
 * **Was an einer Belegung hinausgeht** — der Abschnitt über der Unterschrift
 * (22.09.2026).
 *
 * Luca: *„Wäre ja auch sinnvoll, dass der Kunde bei der Übergabe bestätigt,
 * dass das alles stimmt."* Dafür muss dastehen, was „das alles" ist: Bis dahin
 * zeichnete er unter Zähler, Tank und Fotos — nie unter das Gerät, das er
 * bekommt.
 */
export type Uebergabeinhalt = {
  ausstattung: string | null;
  anbau: string[];
  zubehoer: string[];
  bediener: string[];
  vonIso: string;
  bisIso: string;
  ort: string | null;
};

export async function uebergabeinhalt(belegungId: string): Promise<Uebergabeinhalt | null> {
  // Im Attrappenbetrieb hängt an keiner Belegung eine Position — der Abschnitt
  // bleibt dann weg, statt etwas Erfundenes zu zeigen.
  if (ATTRAPPE) return null;
  const roh = await hole<Roh>(`/handy/inhalt/${encodeURIComponent(belegungId)}`);
  return {
    ausstattung: txt(roh["ausstattung"]) || null,
    anbau: alsListe(roh["anbau"]).map(txt).filter(Boolean),
    zubehoer: alsListe(roh["zubehoer"]).map(txt).filter(Boolean),
    bediener: alsListe(roh["bediener"]).map(txt).filter(Boolean),
    vonIso: txt(roh["von"]),
    bisIso: txt(roh["bis"]),
    ort: txt(roh["ort"]) || null,
  };
}

export async function vorbereitungsblatt(belegungId: string): Promise<Vorbereitungsblatt> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Vorbereitung.");
  const roh = await hole<Roh>(`/vorbereitungen/${encodeURIComponent(belegungId)}`);
  const b = alsObjekt(roh["belegung"]);
  return {
    belegung: {
      id: txt(b["id"]),
      ressourceId: txt(b["ressource_id"]),
      kennung: txt(b["kennung"]),
      typ: txt(b["typ"]),
      kunde: txt(b["kunde"]),
      ort: txt(b["ort"]),
      ortschaft: txt(b["ortschaft"]),
      vorgang: txt(b["vorgang"]),
      ...(txt(b["hofhinweis"]) ? { hofhinweis: txt(b["hofhinweis"]) } : {}),
      vonIso: txt(b["planned_from"]) || null,
      bisIso: txt(b["planned_until"]) || null,
      ausgegeben: b["ausgegeben"] === true,
      bediener: alsListe(b["bediener"]).map(alsPerson).filter((p): p is Beteiligter => p !== null),
    },
    fragen: alsListe(roh["fragen"]).map(alsGeltendeFrage),
    vorbereitung: alsVorbereitung(roh["vorbereitung"] ? alsObjekt(roh["vorbereitung"]) : null),
    darfVorbereiten: roh["darf_vorbereiten"] === true,
    darfMangelErledigen: roh["darf_mangel_erledigen"] === true,
    // Nur ein ausdrückliches „nein" nimmt die Pflicht zurück (siehe oben).
    zaehlerPflicht: roh["zaehler_pflicht"] !== false,
    pflichtfotos: alsPflichtfotoliste(roh["pflichtfotos"]),
  };
}

export type Vorbereitungseingabe = {
  antworten: { frageId: string; wert: Antwortwert }[];
  notiz: string;
  fotos: string[];
};

export async function vorbereitungErledigen(belegungId: string, e: Vorbereitungseingabe): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>(`/vorbereitungen/${encodeURIComponent(belegungId)}`, {
    antworten: e.antworten.map((a) => ({ frage_id: a.frageId, wert: a.wert })),
    ...(e.notiz.trim() ? { notiz: e.notiz.trim() } : {}),
    fotos: e.fotos,
  });
}

/**
 * **Die Vorbereitung im Übergabeblatt am Schreibtisch nachziehen** (P6-04,
 * 05.10.2026) — dieselbe Nutzlast wie am Telefon (`vorbereitungNutzlast`),
 * direkt und nicht über die Warteschlange: Am Schreibtisch gibt es kein
 * Funkloch, und das Protokoll geht erst danach hinaus.
 */
export async function vorbereitungNachziehen(belegungId: string, nutzlast: Record<string, unknown>): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>(`/vorbereitungen/${encodeURIComponent(belegungId)}`, nutzlast);
}

export { nachziehzeitpunkt } from "./nachziehzeit";

/* ── Am Vorgang: ab wann, und vorziehen (Migration 0128) ────────────── */

/** Die Vorbereitung einer Maschine, vom Vorgang aus gesehen. */
export type Vorbereitungsstand = {
  belegungId: string;
  kennung: string;
  typ: string;
  beginn: string | null;
  ausgegeben: boolean;
  /** Steht sie im Pool — nach der Regel des Betriebs oder vorgezogen? */
  sichtbar: boolean;
  /** Ab wann die Regel sie hinausgibt. */
  ab: string | null;
  vorgezogenAm: string | null;
  erledigt: boolean;
  /** Wann und von wem erledigt (L1) — leer, solange sie aussteht. */
  erledigtAm: string | null;
  erledigtVon: string | null;
  mangel: boolean;
  genommen: { id: string; name: string } | null;
};

export async function vorbereitungenDesVorgangs(vorgang: string): Promise<Vorbereitungsstand[]> {
  if (ATTRAPPE) return [];
  const roh = await hole<Roh>(`/vorbereitungen?vorgang=${encodeURIComponent(vorgang)}`);
  return alsListe(roh["zeilen"]).map((z) => {
    const g = alsObjekt(z["genommen"]);
    return {
      belegungId: txt(z["belegung_id"]),
      kennung: txt(z["kennung"]),
      typ: txt(z["typ"]),
      beginn: txt(z["beginn"]) || null,
      ausgegeben: z["ausgegeben"] === true,
      sichtbar: z["sichtbar"] === true,
      ab: txt(z["ab"]) || null,
      vorgezogenAm: txt(z["vorgezogen_am"]) || null,
      erledigt: z["erledigt"] === true,
      erledigtAm: txt(z["erledigt_am"]) || null,
      erledigtVon: txt(z["erledigt_von"]) || null,
      mangel: z["mangel"] === true,
      genommen: txt(g["id"]) ? { id: txt(g["id"]), name: txt(g["name"]) } : null,
    };
  });
}

/** Die Vorbereitung jetzt in den Pool — Disposition und Verwaltung (0128). */
export async function vorbereitungAnstossen(belegungId: string): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>(`/vorbereitungen/${encodeURIComponent(belegungId)}/anstossen`);
}

export async function mangelErledigen(belegungId: string): Promise<void> {
  if (ATTRAPPE) return;
  await sende<Roh>(`/vorbereitungen/${encodeURIComponent(belegungId)}/mangel`);
}
